const EMPTY_ARRAY: readonly [] = []

export function safeClone<T>(array: ReadonlyArray<T>): T[] {
  return [...(array ?? [])]
}

/**
 * Retorna um array vazio imutável com tipagem. Exemplo:
 *
 * ```ts
 * const myList = someList ?? emptyArrayOf<SomeListModel>();
 * ```
 */
export const emptyArrayOf = <T>(): ReadonlyArray<T> => EMPTY_ARRAY as ReadonlyArray<T>

/**
 * Particiona um array em dois grupos baseado em um predicado.
 *
 * Itera sobre o array fornecido e separa os elementos em dois arrays distintos:
 * o primeiro contém os elementos que satisfazem o predicado, e o segundo contém
 * os elementos que não satisfazem.
 *
 * Aceita `null`/`undefined` como entrada, retornando `[[], []]` — mesmo
 * comportamento do `partition` do lodash, evitando regressões nos pontos que
 * passam coleções potencialmente indefinidas.
 *
 * O predicado pode retornar qualquer valor truthy/falsy (não apenas `boolean`),
 * sendo avaliado por veracidade — mesmo comportamento do `partition` do lodash.
 *
 * @template T - O tipo dos elementos no array
 * @param array - O array a ser particionado (aceita `null`/`undefined`)
 * @param predicate - Função que determina o critério de separação dos itens
 * @returns Uma tupla onde o primeiro elemento é o array com itens que satisfazem
 *          o predicado e o segundo elemento é o array com itens que não satisfazem
 */
export function partition<T>(array: ReadonlyArray<T> | null | undefined, predicate: (item: T) => unknown): [T[], T[]] {
  const groupA: T[] = []
  const groupB: T[] = []

  for (const item of array ?? []) {
    if (predicate(item)) {
      groupA.push(item)
    } else {
      groupB.push(item)
    }
  }

  return [groupA, groupB]
}

type Falsy = false | null | undefined | 0 | ''

/**
 * Remove todos os valores falsy (`false`, `null`, `undefined`, `0`, `''`, `NaN`)
 * de um array, substituindo o `compact` do lodash por implementação nativa.
 *
 * @template T - O tipo dos elementos restantes no array
 * @param array - O array a ser filtrado (aceita `null`/`undefined`, retornando `[]`)
 * @returns Um novo array contendo apenas os valores truthy
 */
export function compact<T>(array: ReadonlyArray<T | Falsy> | null | undefined): T[] {
  return (array ?? []).filter(Boolean) as T[]
}

/**
 * Remove itens duplicados por `id`, mantendo a primeira ocorrência e a ordem original.
 * Itens nulos ou indefinidos são ignorados.
 */
export const dedupById = <T extends { id: ID }>(items: ReadonlyArray<T | null | undefined>): T[] => {
  const seen = new Set<ID>()
  const out: T[] = []
  items.forEach((item) => {
    if (item && !seen.has(item.id)) {
      seen.add(item.id)
      out.push(item)
    }
  })
  return out
}

/**
 * Retorna os itens cujo `id` não está presente no conjunto informado, preservando a ordem original.
 */
export const excludeIds = <T extends { id: ID }>(items: ReadonlyArray<T>, ids: ReadonlySet<ID>): T[] =>
  items.filter((it) => !ids.has(it.id))

/**
 * Verifica se `item` é o único elemento do array que satisfaz o predicado — ou seja,
 * se `item` satisfaz o predicado e nenhum outro item (comparado por referência) também satisfaz.
 *
 * Útil para decidir se remover um item é a última ocorrência que sustenta alguma condição
 * dependente do grupo, evitando disparar um efeito (ex.: um aviso de exclusão) quando ainda
 * restam outros itens que satisfazem o mesmo predicado.
 *
 * @template T - O tipo dos elementos no array
 * @param array - O array a ser verificado (aceita `null`/`undefined`)
 * @param item - O item cuja unicidade em relação ao predicado será verificada
 * @param predicate - Função que determina se um item satisfaz a condição observada
 * @returns `true` se `item` satisfaz o predicado e nenhum outro item do array também satisfaz
 */
export function isOnlyMatching<T>(
  array: ReadonlyArray<T> | null | undefined,
  item: T,
  predicate: (item: T) => unknown
): boolean {
  return !!predicate(item) && (array ?? []).every((other) => other === item || !predicate(other))
}

/**
 * Gera uma sequência de números de `start` (inclusive) até `end` (exclusive),
 * incrementando por `step`, substituindo o `range` do lodash por implementação nativa.
 *
 * Quando `end` é omitido, gera de `0` até `start` (exclusive) — mesmo comportamento
 * do `_.range`. Quando `step` é omitido, assume `1` se `start < end`, ou `-1` caso
 * contrário, permitindo sequências decrescentes.
 */
export function range(start: number, end?: number, step?: number): number[] {
  let from = start
  let to = end

  if (to === undefined) {
    to = from
    from = 0
  }

  const clampedStep = from < to ? 1 : -1
  const actualStep = step ?? clampedStep
  const length = Math.max(Math.ceil((to - from) / (actualStep || 1)), 0)
  const result: number[] = []

  for (let i = 0; i < length; i++) {
    result.push(from)
    from += actualStep
  }

  return result
}

function extremumBy<T>(
  array: ReadonlyArray<T> | null | undefined,
  iteratee: (item: T) => number,
  isBetter: (candidate: number, current: number) => boolean
): T | undefined {
  let result: T | undefined
  let best: number | undefined

  for (const item of array ?? []) {
    const value = iteratee(item)
    if (!isComparable(value) || (best !== undefined && !isBetter(value, best))) continue
    result = item
    best = value
  }

  return result
}

/**
 * Retorna o item de `array` com o menor valor computado por `iteratee`,
 * substituindo o `minBy` do lodash por implementação nativa.
 *
 * Assim como no `_.minBy`, itens cujo `iteratee` retorna `null`, `undefined`,
 * `NaN` ou símbolo são ignorados. Aceita `null`/`undefined`/array vazio,
 * retornando `undefined`. Quando há empate, prevalece a primeira ocorrência.
 */
export function minBy<T>(array: ReadonlyArray<T> | null | undefined, iteratee: (item: T) => number): T | undefined {
  return extremumBy(array, iteratee, (candidate, current) => candidate < current)
}

/**
 * Retorna o item de `array` com o maior valor computado por `iteratee`,
 * substituindo o `maxBy` do lodash por implementação nativa.
 *
 * Assim como no `_.maxBy`, itens cujo `iteratee` retorna `null`, `undefined`,
 * `NaN` ou símbolo são ignorados. Aceita `null`/`undefined`/array vazio,
 * retornando `undefined`. Quando há empate, prevalece a primeira ocorrência.
 */
export function maxBy<T>(array: ReadonlyArray<T> | null | undefined, iteratee: (item: T) => number): T | undefined {
  return extremumBy(array, iteratee, (candidate, current) => candidate > current)
}

/**
 * Retorna o maior valor da coleção sem converter o elemento retornado.
 * Assim como no lodash, ignora valores nulos, `undefined`, `NaN` e símbolos,
 * retorna `undefined` para coleções vazias e mantém a primeira ocorrência em empates.
 */
export function max<T>(array: ReadonlyArray<T> | null | undefined): T | undefined {
  let result: T | undefined

  for (const item of array ?? []) {
    if (!isComparable(item) || (result !== undefined && !isGreater(item, result))) continue
    result = item
  }

  return result
}

function isComparable(value: unknown): boolean {
  return value != null && typeof value !== 'symbol' && value === value
}

function isGreater(value: unknown, other: unknown): boolean {
  const first = value instanceof Date ? value.valueOf() : value
  const second = other instanceof Date ? other.valueOf() : other
  if (typeof first === 'string' && typeof second === 'string') return first > second
  return Number(first) > Number(second)
}

export type SortOrder = 'asc' | 'desc'
export type SortIteratee<T> = ((item: T) => unknown) | keyof T

/**
 * Ordena uma coleção por um ou mais critérios, sem mutar o array original.
 * Critérios sem direção explícita usam ordem ascendente e empates preservam a ordem original.
 */
export function orderBy<T>(
  collection: ReadonlyArray<T> | Readonly<Record<string, T>> | null | undefined,
  iteratees: SortIteratee<T> | ReadonlyArray<SortIteratee<T>>,
  orders: SortOrder | ReadonlyArray<SortOrder> = 'asc'
): T[] {
  const criteria = Array.isArray(iteratees) ? iteratees : [iteratees]
  const directions = Array.isArray(orders) ? orders : [orders]

  return collectionValues(collection)
    .map((value, index) => ({ value, index, criteria: criteria.map((iteratee) => resolveCriteria(value, iteratee)) }))
    .sort((first, second) => compareItems(first, second, directions))
    .map(({ value }) => value)
}

function collectionValues<T>(collection: ReadonlyArray<T> | Readonly<Record<string, T>> | null | undefined): T[] {
  if (!collection) return []
  return Array.isArray(collection) ? [...collection] : Object.values(collection)
}

function resolveCriteria<T>(item: T, iteratee: SortIteratee<T>): unknown {
  return typeof iteratee === 'function' ? iteratee(item) : item[iteratee]
}

function compareItems(
  first: { criteria: unknown[]; index: number },
  second: { criteria: unknown[]; index: number },
  orders: ReadonlyArray<SortOrder>
): number {
  for (let index = 0; index < first.criteria.length; index++) {
    const result = compareAscending(first.criteria[index], second.criteria[index])
    if (result) return orders[index] === 'desc' ? -result : result
  }
  return first.index - second.index
}

function compareAscending(value: unknown, other: unknown): number {
  if (value === other) return 0
  const valueFlags = comparisonFlags(value)
  const otherFlags = comparisonFlags(other)
  if (shouldComeAfter(value, other, valueFlags, otherFlags)) return 1
  if (shouldComeAfter(other, value, otherFlags, valueFlags)) return -1
  return 0
}

type ComparisonFlags = ReturnType<typeof comparisonFlags>

function comparisonFlags(value: unknown) {
  return {
    defined: value !== undefined,
    null: value === null,
    reflexive: value === value,
    symbol: typeof value === 'symbol',
  }
}

function shouldComeAfter(value: unknown, other: unknown, valueFlags: ComparisonFlags, otherFlags: ComparisonFlags) {
  const greater = !otherFlags.null && !otherFlags.symbol && !valueFlags.symbol && isGreater(value, other)
  const symbol =
    valueFlags.symbol && otherFlags.defined && otherFlags.reflexive && !otherFlags.null && !otherFlags.symbol
  const nullValue = valueFlags.null && otherFlags.defined && otherFlags.reflexive
  const undefinedValue = !valueFlags.defined && otherFlags.reflexive
  return greater || symbol || nullValue || undefinedValue || !valueFlags.reflexive
}

/**
 * Retorna os itens de `array` que não estão presentes em `values`, substituindo
 * o `difference` do lodash por implementação nativa.
 *
 * A comparação é feita por igualdade estrita (`SameValueZero`), preservando a
 * ordem original de `array`. Aceita `null`/`undefined`, retornando `[]`.
 */
export function difference<T>(
  array: ReadonlyArray<T> | null | undefined,
  values: ReadonlyArray<T> | null | undefined
): T[] {
  const exclude = new Set(values ?? [])
  return (array ?? []).filter((item) => !exclude.has(item))
}

export function createArray<T>(length: number, factory: (index: number) => T): T[] {
  return Array.from({ length }, (_, index) => factory(index))
}
